/**
 * OraFireflyAnimated — branded mascot for workflow loading and upload screens.
 *
 * Wraps the static ``OraFireflyIcon`` with:
 *   • a slow lissajous orbit (6 s loop) so the firefly feels "alive, exploring"
 *   • a soft sunrise halo that pulses with the active stage's tone
 *   • a 400 ms shudder when ``tone === "error"`` (failed-state hand-off)
 *
 * ``prefers-reduced-motion`` short-circuits the orbit and halo pulse — the
 * firefly stays still, halo becomes a static glow.
 */
import OraFireflyIcon from "~/common/components/svgs/OraFireflyIcon";
import { usePrefersReducedMotion } from "~/common/hooks/usePrefersReducedMotion";

export type FireflyTone =
  | "running"
  | "complete"
  | "error"
  // Additive voice tones (docked hands-free firefly), ported with the oraChat
  // V2 runtime. Existing consumers pass only running/complete/error and are
  // unaffected — these add halo colours (green listening / blue speaking); the
  // orbit/shudder logic below is unchanged (only "error" shudders).
  | "listening"
  | "speaking";

interface Props {
  /** Visual diameter in px (drives both the icon and halo). Default 120. */
  size?: number;
  /** Drives halo color + animation (running pulses; error triggers shudder). */
  tone?: FireflyTone;
  className?: string;
}

const TONE_HALO: Record<FireflyTone, string> = {
  running:
    "bg-[radial-gradient(circle,_rgba(255,198,0,0.55)_0%,_rgba(255,198,0,0)_70%)]",
  complete:
    "bg-[radial-gradient(circle,_rgba(7,148,85,0.45)_0%,_rgba(7,148,85,0)_70%)]",
  error:
    "bg-[radial-gradient(circle,_rgba(217,45,32,0.45)_0%,_rgba(217,45,32,0)_70%)]",
  // green — the hands-free "listening / talk to me" state.
  listening:
    "bg-[radial-gradient(circle,_rgba(14,122,61,0.5)_0%,_rgba(14,122,61,0)_70%)]",
  // blue, stronger centre — the hands-free "speaking" state.
  speaking:
    "bg-[radial-gradient(circle,_rgba(21,112,239,0.6)_0%,_rgba(21,112,239,0)_70%)]",
};

export function OraFireflyAnimated({
  size = 120,
  tone = "running",
  className = "",
}: Props) {
  const reduced = usePrefersReducedMotion();
  const haloSize = Math.round(size * 1.6);

  // Reduced motion → static halo + still firefly. Error tone always shudders.
  const fireflyAnimation = reduced
    ? ""
    : tone === "error"
      ? "animate-ora-shudder"
      : "animate-firefly-orbit";
  const haloAnimation = reduced ? "" : "animate-firefly-halo";

  return (
    <div
      data-testid="ora-firefly-animated"
      data-motion={reduced ? "reduced" : "full"}
      data-tone={tone}
      className={`relative flex items-center justify-center ${className}`}
      style={{ width: haloSize, height: haloSize }}
      aria-hidden="true"
    >
      <div
        data-testid="ora-firefly-halo"
        className={`absolute inset-0 rounded-full ${TONE_HALO[tone]} ${haloAnimation}`}
      />
      <div
        data-testid="ora-firefly-orbit"
        className={`relative ${fireflyAnimation}`}
        style={{ width: size, height: size }}
      >
        <OraFireflyIcon className="w-full h-full" />
      </div>
    </div>
  );
}
